import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import BalanceOutlinedIcon from "@mui/icons-material/BalanceOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import { gql, useQuery } from "@apollo/client";
import { accountClient } from "state/apolloClient";

const GET_MEMBER_360_SNAPSHOT = gql`
  query Member360Snapshot($userId: Int, $year: String) {
    member360Snapshot(userId: $userId, year: $year) {
      availableContributionAfterLiabilities
      contributionOutstandingToDate
      contributionPaidToDate
      expectedContributionToDate
      expectedTotalContributionForYear
      groupId
      isContributionUpToDate
      loansGuaranteedCount
      loansTakenCount
      totalContributionSoFar
      totalGuarantorshipAmount
      totalLoanPrincipalTaken
      totalLoansPaid
      totalLoansPending
      userId
      year
    }
  }
`;

const money = (value) => `KES ${Number(value || 0).toLocaleString()}`;
const asPercent = (value) => `${Math.max(0, Math.min(Number(value) || 0, 100))}%`;

const InsightCard = ({ icon, title, value, subtitle, color = "text.primary" }) => (
  <Card sx={{ borderRadius: 3, height: "100%" }}>
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.25}>
        <Typography variant="caption" sx={{ opacity: 0.75 }}>
          {title}
        </Typography>
        {icon}
      </Stack>
      <Typography variant="h5" fontWeight={800} color={color}>
        {value}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          {subtitle}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

const Member360Modal = ({ open, onClose, member }) => {
  const currentYear = String(new Date().getFullYear());
  const yearVariable = /^\d{4}$/.test(currentYear) ? currentYear : null;
  const userId = Number(member?.id);

  const {
    data: snapshotData,
    loading: snapshotLoading,
    error: snapshotError,
  } = useQuery(GET_MEMBER_360_SNAPSHOT, {
    client: accountClient,
    variables: { userId, year: yearVariable },
    skip: !open || !userId,
    fetchPolicy: "cache-and-network",
  });

  const metrics = useMemo(() => {
    const snapshot = snapshotData?.member360Snapshot;
    const expectedTotal = Number(snapshot?.expectedTotalContributionForYear || 0);
    const expectedToDate = Number(snapshot?.expectedContributionToDate || 0);
    const paidToDate = Number(snapshot?.contributionPaidToDate || 0);
    const outstandingToDate = Number(snapshot?.contributionOutstandingToDate || 0);
    const totalContributedSoFar = Number(snapshot?.totalContributionSoFar || 0);
    const totalLoanPrincipalTaken = Number(snapshot?.totalLoanPrincipalTaken || 0);
    const totalLoansPaid = Number(snapshot?.totalLoansPaid || 0);
    const totalLoansPending = Number(snapshot?.totalLoansPending || 0);
    const totalGuarantorshipAmount = Number(snapshot?.totalGuarantorshipAmount || 0);
    const availableContributionAfterLiabilities = Number(snapshot?.availableContributionAfterLiabilities || 0);
    const annualProgressPct = expectedTotal
      ? Math.min(100, Math.round((totalContributedSoFar / expectedTotal) * 100))
      : 0;
    const toDateProgressPct = expectedToDate
      ? Math.min(100, Math.round((paidToDate / expectedToDate) * 100))
      : snapshot?.isContributionUpToDate
        ? 100
        : 0;

    const loanSettlementPct = totalLoanPrincipalTaken
      ? Math.min(100, Math.round((totalLoansPaid / totalLoanPrincipalTaken) * 100))
      : 0;

    const annualContributionGap = Math.max(expectedTotal - totalContributedSoFar, 0);
    const debtLoad = totalLoanPrincipalTaken ? Math.round((totalLoansPending / totalLoanPrincipalTaken) * 100) : 0;

    return {
      snapshot,
      expectedTotal,
      expectedToDate,
      paidToDate,
      outstandingToDate,
      totalContributedSoFar,
      totalLoanPrincipalTaken,
      totalLoansPaid,
      totalLoansPending,
      totalGuarantorshipAmount,
      availableContributionAfterLiabilities,
      annualContributionGap,
      annualProgressPct,
      toDateProgressPct,
      loanSettlementPct,
      debtLoad,
    };
  }, [snapshotData]);

  if (!member) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center">
          <PersonOutlineIcon />
          <Typography fontWeight={800}>
            Member 360° — {member.firstName} {member.lastName}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {snapshotLoading && <LinearProgress sx={{ mb: 2 }} />}

        {snapshotError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {snapshotError?.message || "Failed to load Member 360 snapshot details."}
          </Alert>
        )}

        {!snapshotLoading && !snapshotError && !metrics.snapshot ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No Member 360 snapshot was returned for this member in {currentYear}.
          </Alert>
        ) : null}

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <InsightCard
              title="Contribution Status To Date"
              value={metrics.snapshot?.isContributionUpToDate ? "On track" : "Behind"}
              subtitle={`${money(metrics.paidToDate)} paid against ${money(metrics.expectedToDate)} expected to date`}
              icon={<SavingsOutlinedIcon fontSize="small" />}
              color={metrics.snapshot?.isContributionUpToDate ? "success.main" : "warning.main"}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InsightCard
              title="Outstanding To Date"
              value={money(metrics.outstandingToDate)}
              subtitle="Amount needed to be fully current for this month"
              icon={<ErrorOutlineOutlinedIcon fontSize="small" />}
              color={metrics.outstandingToDate ? "error.main" : "success.main"}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InsightCard
              title="Annual Contribution Progress"
              value={asPercent(metrics.annualProgressPct)}
              subtitle={`${money(metrics.totalContributedSoFar)} of ${money(metrics.expectedTotal)} annual goal`}
              icon={<TrendingUpIcon fontSize="small" />}
              color={metrics.annualProgressPct >= 70 ? "success.main" : "warning.main"}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InsightCard
              title="Available After Liabilities"
              value={money(metrics.availableContributionAfterLiabilities)}
              subtitle="Contribution balance left after loans + guarantees"
              icon={<AccountBalanceOutlinedIcon fontSize="small" />}
              color={metrics.availableContributionAfterLiabilities > 0 ? "success.main" : "error.main"}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InsightCard
              title="Loan Settlement"
              value={asPercent(metrics.loanSettlementPct)}
              subtitle={`${money(metrics.totalLoansPaid)} repaid`}
              icon={<PaymentsOutlinedIcon fontSize="small" />}
              color={metrics.loanSettlementPct >= 60 ? "success.main" : "warning.main"}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InsightCard
              title="Debt Load"
              value={asPercent(metrics.debtLoad)}
              subtitle={`${money(metrics.totalLoansPending)} pending`}
              icon={<BalanceOutlinedIcon fontSize="small" />}
              color={metrics.debtLoad <= 40 ? "success.main" : "error.main"}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <AccountBalanceWalletOutlinedIcon fontSize="small" />
                  <Typography fontWeight={700}>Contribution Health ({currentYear})</Typography>
                </Stack>

                <Alert severity={metrics.snapshot?.isContributionUpToDate ? "success" : "warning"} sx={{ mb: 1.5 }}>
                  {metrics.snapshot?.isContributionUpToDate
                    ? "This member has settled all contribution dues expected up to the current month based on the schedule due date."
                    : "This member still has contribution dues outstanding for the current point in the year based on the schedule due date."}
                </Alert>

                <Stack spacing={1.25}>
                  <Typography variant="body2">
                    Expected to date: <strong>{money(metrics.expectedToDate)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Paid to date: <strong>{money(metrics.paidToDate)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Outstanding to date: <strong>{money(metrics.outstandingToDate)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Annual goal: <strong>{money(metrics.expectedTotal)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Total contributed so far: <strong>{money(metrics.totalContributedSoFar)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Remaining annual target: <strong>{money(metrics.annualContributionGap)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Available after liabilities: <strong>{money(metrics.availableContributionAfterLiabilities)}</strong>
                  </Typography>

                  <Box>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption">Monthly / to-date contribution progress</Typography>
                      <Typography variant="caption">{asPercent(metrics.toDateProgressPct)}</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={metrics.toDateProgressPct} sx={{ mb: 1.25 }} />

                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption">Annual contribution progress</Typography>
                      <Typography variant="caption">{asPercent(metrics.annualProgressPct)}</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={metrics.annualProgressPct} />
                  </Box>

                  <Box>
                    {metrics.snapshot?.isContributionUpToDate ? (
                      <Chip color="success" size="small" label="Up to date with monthly contributions" />
                    ) : (
                      <Chip
                        color="warning"
                        size="small"
                        variant="outlined"
                        label="Monthly contribution payments are lagging"
                      />
                    )}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <TrendingUpIcon fontSize="small" />
                  <Typography fontWeight={700}>Loans & Guarantorship Overview</Typography>
                </Stack>

                <Stack spacing={1}>
                  <Typography variant="body2">
                    Loans taken: <strong>{Number(metrics.snapshot?.loansTakenCount || 0)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Guarantees issued: <strong>{Number(metrics.snapshot?.loansGuaranteedCount || 0)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Principal borrowed: <strong>{money(metrics.totalLoanPrincipalTaken)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Total repaid: <strong>{money(metrics.totalLoansPaid)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Pending balance: <strong>{money(metrics.totalLoansPending)}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Total guarantorship value: <strong>{money(metrics.totalGuarantorshipAmount)}</strong>
                  </Typography>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="caption">Loan settlement progress</Typography>
                    <Typography variant="caption">{asPercent(metrics.loanSettlementPct)}</Typography>
                  </Stack>
                  <LinearProgress variant="determinate" value={metrics.loanSettlementPct} sx={{ mb: 1 }} />

                  <Tooltip title="Quick decision cue: prioritize contribution headroom after liabilities, then debt load and whether the member is current on monthly contribution dues.">
                    <Chip
                      size="small"
                      color={metrics.debtLoad <= 40 && metrics.snapshot?.isContributionUpToDate ? "success" : "warning"}
                      label={
                        metrics.debtLoad <= 40 && metrics.snapshot?.isContributionUpToDate
                          ? "Healthy financial posture"
                          : "Needs attention before new obligations"
                      }
                    />
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {!snapshotLoading && metrics.snapshot ? (
          <Stack direction="row" spacing={1} mt={2}>
            <Chip size="small" label={`Group ID: ${metrics.snapshot.groupId ?? "-"}`} variant="outlined" />
            <Chip size="small" label={`Snapshot Year: ${metrics.snapshot.year ?? currentYear}`} variant="outlined" />
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Member360Modal;
